/**
 * Store types — plain data. React context lives in `store/context.tsx`.
 *
 * Design (see wp7-client-ui-input.md § "State management"):
 *   • Snapshots do NOT flow through React state. Only phase changes,
 *     rejections, connection status, matchId, ownSeat, hudTick reach
 *     React. Snapshots go straight to the WS-backed `SnapshotSource`
 *     (which pumps WP6's ring buffer) — bypassing React at 30 Hz.
 *   • The HUD samples the latest snapshot at 10 Hz (~3 frames) via a
 *     tick counter in the store — the HUD components read the
 *     current snapshot from a small ref-like sampler.
 */

import type {
  AbilityId,
  ChampionId,
  MatchId,
  MatchPhase,
  TeamIndex,
  Tick,
  WireBotEntry,
  WireDamageEntry,
  WireRosterEntry,
  WireSimState,
} from "@pit/protocol";
import type { CastBlock, CastBlockReason } from "../input/castability.js";

export type ConnectionKind = "idle" | "connecting" | "welcomed" | "protocolMismatch" | "closed";

export interface ConnectionState {
  readonly kind: ConnectionKind;
  readonly sessionId: string | null;
  /** Populated when `kind === "protocolMismatch"`. */
  readonly serverVersion: string | null;
  /** True after a graceful welcome; false after unclean drop. */
  readonly cleanClose: boolean;
  /** #497: resolved server-side tester flag (`welcome.testerFlagged`),
   *  mirrored here so tester-gated UI (starting with #499) can read it off
   *  the store. `false` outside `kind === "welcomed"`. */
  readonly testerFlagged: boolean;
}

/** Match phase + winner (mirrors the wire `matchPhase` message). */
export interface PhaseState {
  readonly phase: MatchPhase;
  readonly winner: TeamIndex | null;
  /** The sim's authoritative countdown deadline (#276) — non-null only when
   *  `phase === "countdown"`. Lets `Countdown.tsx` read the true deadline
   *  instead of estimating one from the first countdown snapshot it
   *  happens to observe. */
  readonly endsAtTick: Tick | null;
}

/** A single rejection toast (`inputRejected` from server). */
export interface RejectionEntry {
  readonly id: number;
  readonly reason: string;
  readonly ts: number;
}

/** A local-flash keypress marker used by the action bar to flash red
 *  when a cast keypress got no casting-state within ~3 ticks. Kept in
 *  the store so the reducer owns the timing bookkeeping. */
export interface KeypressFlash {
  readonly slot: number;
  /** ms wall-clock at which the press happened. */
  readonly ts: number;
}

/**
 * #787: a locally-predicted cast failure to show the player ("Not enough
 * energy"), produced by `input/castability.ts`'s shared predicate. Carries the
 * predicate's own `CastBlock` verbatim — the store never re-derives or reshapes
 * it, so a new reason arm needs no change here.
 *
 * DISTINCT FROM `rejections`: those are server `inputRejected` messages, which
 * "should never fire for honest clients" (see `RejectionToast`) — routing
 * routine out-of-resource play through them would destroy that diagnostic's
 * signal value. This is the ordinary-gameplay channel.
 *
 * COALESCED BY REASON (never enqueued): mashing a key the caster can't afford
 * REFRESHES the live entry's `ts` instead of stacking N entries, so the player
 * sees ONE message whose dismissal timer restarts.
 */
export interface CastErrorEntry {
  readonly block: CastBlock;
  /** ms wall-clock of the most recent occurrence (refreshed, not appended). */
  readonly ts: number;
}

/**
 * Store state. Small on purpose — the payload behaviour lives in
 * snapshots (routed to the WP6 renderer directly), never through
 * React state.
 */
export interface StoreState {
  readonly connection: ConnectionState;
  readonly phase: PhaseState;
  readonly matchId: MatchId | null;
  readonly joinUrl: string | null;
  readonly ownSeat: number | null;
  /** The champion the local player selected for this match (create/join/quick).
   *  Presentation-only: lets the renderer map the local seat to its champion
   *  for the 3D character path (#237); `null` until a selection is made. */
  readonly ownChampionId: ChampionId | null;
  /** The exact ability loadout the local player selected (and sent to the
   *  server) for this match — the client's own locally-known `loadout`
   *  array from `HomeScreen`/`JoinScreen`'s picker state (#513), NOT a
   *  server echo. Dispatched in the SAME action as `ownChampionId` so the
   *  two can never go out of sync. Presentation-only: lets `ActionBar` map
   *  its 12 visual bar positions to the abilities actually picked, instead
   *  of the champion's full canonical-slot pool; `null` until a selection
   *  is made (pre-match demo/dev routes fall back to the full pool — see
   *  `ActionBar`). */
  readonly ownLoadout: readonly AbilityId[] | null;
  /** Every seat→champion mapping the server has told us about so far (#244),
   *  from the `matchPhase` roster. Presentation-only: lets the renderer
   *  resolve EVERY seat's champion (not just the local one) for the 3D
   *  character path. Empty until the first `matchPhase` arrives. */
  readonly roster: readonly WireRosterEntry[];
  /** Total damage dealt per seat over the whole match (#471), from the
   *  `matchPhase` message — the post-match damage-meter table's data
   *  source. Non-empty only once a `matchPhase{ended}` has arrived; empty
   *  otherwise (mirrors `roster`'s empty-until-first-`matchPhase` shape). */
  readonly damageDealt: readonly WireDamageEntry[];
  readonly hudTick: number;
  readonly rejections: readonly RejectionEntry[];
  readonly castFlashes: readonly KeypressFlash[];
  /** #787: live locally-predicted cast-failure notices, at most one per
   *  reason (see `CastErrorEntry`). Empty in the common case. */
  readonly castErrors: readonly CastErrorEntry[];
  /** Sampled snapshot for HUD components — updated at 10 Hz, NOT 30 Hz.
   *  UI reads this via the sampler hook (see `store/sampler.ts`). */
  readonly hudSnapshot: WireSimState | null;
  /** True if the join screen tried to auto-join and got no matching
   *  match on the server (or is still waiting for welcome). */
  readonly joinError: string | null;
  /** Last `error` message from the server (code + ts), or null. Surfaced
   *  so a rejected create/join/quickJoin is no longer silently swallowed
   *  and pending-action state can be reset. */
  readonly serverError: { readonly code: string; readonly ts: number } | null;
  /** #499: the promoted, 1v1-eligible bot roster from the last `botList`
   *  reply — the practice-mode setup screen's bot-opponent picker data
   *  source. Empty until requested (or if the manifest has no eligible
   *  entries yet). */
  readonly practiceBots: readonly WireBotEntry[];
  /**
   * #700: the room's actual resolved map slug, from `matchCreated`/
   * `matchPhase` (same field, same value, mirroring `roster`'s "every seat
   * eventually learns it, not just the creator" reach). Presentation-only:
   * `MatchScreen.tsx` looks this up against `ALL_CONTENT_MAPS` to render
   * the ACTUAL arena geometry instead of the hardcoded `bare` fallback.
   * `null` until the first `matchCreated`/`matchPhase` arrives (e.g. the
   * pre-match demo/dev routes, which never send either).
   */
  readonly mapSlug: string | null;
  /**
   * #833: is the in-game chat box open?
   *
   * LOAD-BEARING FOR INPUT SAFETY, not just for rendering a box. While this
   * is `true`, `InputSurface`'s document keydown handler returns before ANY
   * gameplay dispatch, so typing "watch out" cannot fire the abilities bound
   * to w/a/t/c/h. It lives in the shared store (rather than local component
   * state) precisely because `InputSurface` — a sibling, not a parent — is
   * the component that has to honour it.
   *
   * Reset by `match.leave` via `INITIAL_STATE`, so an open box can never
   * survive into the next screen and silently eat input there.
   *
   * #855: CANNOT BE TRUE WHILE `chatEnabled` IS FALSE. The reducer refuses
   * `chat.setOpen` while chat is off and force-closes on `chat.setEnabled`,
   * which is what makes "chat is off but a keystroke got eaten by the chat
   * guard" an unrepresentable state rather than a rule every dispatcher has
   * to remember.
   */
  readonly chatOpen: boolean;
  /**
   * #855: is in-game chat enabled at all? Persisted client preference
   * (`prefs.ts`'s `chatEnabled`), seeded into the store at construction
   * (`main.tsx`) and defaulting to ON.
   *
   * "Off" means off in every direction, with no partial states:
   *   • incoming chat is not rendered (`mount.ts` skips the chat ingest),
   *   • the compose box cannot be opened (`chatOpen` above),
   *   • the `openChat` key binding is inert, so `Enter` falls through to
   *     gameplay exactly as it did pre-#833.
   */
  readonly chatEnabled: boolean;
  /**
   * #855: seats whose chat this client is ignoring, for this session only.
   * Deliberately NOT persisted and NOT sent to the server: this is the
   * minimum-viable "one person is being awful right now" lever, not a social
   * graph. Server-side blocking needs an accounts story and is out of scope.
   *
   * Sorted ascending and duplicate-free (the reducer maintains both), so the
   * value is stable across equivalent mute sets and cheap to compare.
   */
  readonly mutedSeats: readonly number[];
}

export const INITIAL_STATE: StoreState = {
  connection: {
    kind: "idle",
    sessionId: null,
    serverVersion: null,
    cleanClose: false,
    testerFlagged: false,
  },
  phase: { phase: "waiting", winner: null, endsAtTick: null },
  matchId: null,
  joinUrl: null,
  ownSeat: null,
  ownChampionId: null,
  ownLoadout: null,
  roster: [],
  damageDealt: [],
  hudTick: 0,
  rejections: [],
  castFlashes: [],
  castErrors: [],
  hudSnapshot: null,
  joinError: null,
  serverError: null,
  practiceBots: [],
  mapSlug: null,
  chatOpen: false,
  chatEnabled: true,
  mutedSeats: [],
};

/** Actions. Discriminated on `type` for reducer exhaustiveness. */
export type Action =
  | { readonly type: "connection.status"; readonly next: ConnectionState }
  | {
      readonly type: "match.created";
      readonly matchId: MatchId;
      readonly joinUrl: string;
      /** #700: see `StoreState.mapSlug`. */
      readonly mapSlug: string;
    }
  | {
      readonly type: "match.joined";
      readonly matchId: MatchId;
      readonly ownSeat: number;
    }
  | {
      readonly type: "match.phase";
      readonly phase: MatchPhase;
      readonly winner: TeamIndex | null;
      readonly roster: readonly WireRosterEntry[];
      readonly endsAtTick: Tick | null;
      readonly damageDealt: readonly WireDamageEntry[];
      /** #700: see `StoreState.mapSlug`. */
      readonly mapSlug: string;
    }
  | { readonly type: "match.leave" }
  | {
      readonly type: "match.champion";
      readonly championId: ChampionId;
      /** #513: the loadout sent alongside this champion pick — see
       *  `StoreState.ownLoadout`'s doc comment. */
      readonly loadout: readonly AbilityId[];
    }
  | { readonly type: "hud.sample"; readonly snapshot: WireSimState }
  | { readonly type: "hud.tick" }
  | { readonly type: "rejection.add"; readonly reason: string; readonly ts: number }
  | { readonly type: "rejection.drop"; readonly id: number }
  | { readonly type: "castFlash.add"; readonly slot: number; readonly ts: number }
  | { readonly type: "castFlash.drop"; readonly ts: number }
  /** #787: show (or refresh) the notice for a locally-predicted cast failure. */
  | { readonly type: "castError.add"; readonly block: CastBlock; readonly ts: number }
  /** #787: dismiss the notice for `reason` — dispatched by the notice's own
   *  auto-dismiss timer (it owns its lifetime; see `CastErrorNotice`). */
  | { readonly type: "castError.drop"; readonly reason: CastBlockReason }
  | { readonly type: "joinError.set"; readonly reason: string | null }
  | { readonly type: "serverError"; readonly code: string; readonly ts: number }
  | { readonly type: "serverError.clear" }
  | { readonly type: "practice.botList"; readonly bots: readonly WireBotEntry[] }
  /** #833: open/close the in-game chat box. See `StoreState.chatOpen`. */
  | { readonly type: "chat.setOpen"; readonly open: boolean }
  /** #855: turn in-game chat completely on/off. See `StoreState.chatEnabled`. */
  | { readonly type: "chat.setEnabled"; readonly enabled: boolean }
  /** #855: mute/unmute one seat's chat for the rest of this session. See
   *  `StoreState.mutedSeats`. */
  | {
      readonly type: "chat.setSeatMuted";
      readonly seat: number;
      readonly muted: boolean;
    };
